import React, {useEffect, useMemo, useState} from "react";
import {Spin} from "antd";
import _ from "lodash";
import {
    AuthorizedPoolFilter,
    CityFilter,
    CompanyFilter,
    IntegrationTypeFilter,
    PoolActivityFilter,
    PoolAvailableRateFilter,
    SubCompanyFilter
} from "../../../_defaultComponents/FilterComponents";
import {cities} from "../../../../constants/constants";
import {useCitiesForPools} from "../../../../hooks/useCitiesForPools";
import {useFetch} from "../../../../hooks/useFetch";
import {useStore} from "hooks/useStore";
import {FilterLabel} from "../../../_defaultComponents/FilterComponents/FilterComponentStyles";
import {isRealPool} from "../../../../utils/poolUtils";

const Filters = ({setCurrentPage, filters, setFilters}) => {
    const stores = useStore();
    const isAgency = stores.meStore.checkRole("isAgency");
    const [city, setCity] = useState(filters.city);
    const [availableRate, setAvailableRate] = useState(filters.availableRate);
    // const [slotRate, setSlotRate] = useState(filters.slotRate);
    const [activity, setActivity] = useState(filters.activity);
    const [integrationType, setIntegrationType] = useState(filters.integrationType);
    const [poolIds, setPoolIds] = useState(filters.poolIds);
    const [companyId, setCompanyId] = useState(filters.companyId);
    const [subCompanyId, setSubCompanyId] = useState(filters.subCompanyId);
    const {loading, cities: citiesData} = useCitiesForPools();
    const restrictedCities = useMemo(() => {
        if (!citiesData) return;
        return cities
            .filter(c => !citiesData.includes(c?.name?.toLocaleLowerCase("tr-TR")))
            .map(c => c?.name || "");
    }, [citiesData]);

    // Acente, "Dedike Entegrasyon" seçtiğinde Firma/Alt Firma filtresiyle daraltabilsin diye
    // dedike entegrasyon havuzlarının listesi çekilir; firma seçimi buradan havuza çözümlenir.
    const [, dedicatedIntegrationPools] = useFetch({
        defaultDataValue: [],
        fetchFn: stores.poolStore.getPoolList.bind(null, {
            limit: 1000,
            page: 1,
            integrationType: "dedicatedIntegration"
        }),
        dependencies: [isAgency, integrationType],
        condition: isAgency && integrationType === "dedicatedIntegration"
    });

    const matchedDedicatedIntegrationPoolIds = useMemo(() => {
        if (!isAgency || integrationType !== "dedicatedIntegration" || !companyId) return [];
        return (dedicatedIntegrationPools?.data || [])
            .filter(
                pool =>
                    pool.companies?.some(company => company._id === companyId) &&
                    (!subCompanyId ||
                        pool.subCompanies?.some(subCompany => subCompany._id === subCompanyId))
            )
            .map(pool => pool._id);
    }, [isAgency, integrationType, companyId, subCompanyId, dedicatedIntegrationPools]);

    const filterCompanyByDedicatedIntegrationPools = company =>
        (dedicatedIntegrationPools?.data || []).some(pool =>
            pool.companies?.some(poolCompany => poolCompany._id === company._id)
        );

    const effectivePoolIds =
        isAgency && integrationType === "dedicatedIntegration"
            ? matchedDedicatedIntegrationPoolIds
            : poolIds;

    const handleIntegrationTypeChange = value => {
        setIntegrationType(value);
        if (isAgency && value !== "dedicatedIntegration") {
            setCompanyId(undefined);
            setSubCompanyId(undefined);
        }
        if (isAgency && value !== "pool") {
            setPoolIds([]);
        }
    };

    const handleCompanyChange = value => {
        setCompanyId(value);
        setSubCompanyId(undefined);
    };

    useEffect(() => {
        setCurrentPage(1);
    }, [city, availableRate, activity, integrationType, effectivePoolIds, companyId, subCompanyId]);

    useEffect(() => {
        const newFilters = {
            city,
            availableRate,
            activity,
            integrationType,
            poolIds: effectivePoolIds,
            companyId,
            subCompanyId
        };
        if (JSON.stringify(filters) !== JSON.stringify(newFilters)) {
            setFilters(newFilters);
        }
    }, [city, availableRate, activity, integrationType, effectivePoolIds, companyId, subCompanyId]);

    return (
        <React.Fragment>
            <div className="filter-item">
                <FilterLabel>Şehir</FilterLabel>
                <Spin spinning={loading}>
                    <CityFilter
                        value={city}
                        onChange={setCity}
                        restrictedCities={restrictedCities}
                        showLabel={false}
                        selectMarginBottom="0px"
                    />
                </Spin>
            </div>
            <div className="filter-item">
                <FilterLabel>Havuz Aktiflik</FilterLabel>
                <PoolActivityFilter
                    value={activity}
                    onChange={setActivity}
                    showLabel={false}
                    selectMarginBottom="0px"
                />
            </div>
            <div className="filter-item">
                <FilterLabel>Yoğunluk Oranı</FilterLabel>
                <PoolAvailableRateFilter
                    value={availableRate}
                    onChange={setAvailableRate}
                    showLabel={false}
                    selectMarginBottom="0px"
                />
            </div>
            {/* <div className="filter-item">
                <PoolSlotRateFilter value={slotRate} onChange={setSlotRate} />
            </div> */}
            <div className="filter-item">
                <FilterLabel>Entegrasyon Tipi</FilterLabel>
                <IntegrationTypeFilter
                    value={integrationType}
                    onChange={handleIntegrationTypeChange}
                    showAllOption={!isAgency}
                    excludedTypes={isAgency ? ["dedicated"] : []}
                    showLabel={false}
                    selectMarginBottom="0px"
                />
            </div>
            {stores.meStore.checkRole("canSearchPools") &&
                (!isAgency || integrationType === "pool") && (
                    <div className="filter-item">
                        <FilterLabel>Havuz Bölgesi</FilterLabel>
                        <AuthorizedPoolFilter
                            value={poolIds}
                            onChange={setPoolIds}
                            mode="multiple"
                            showLabel={false}
                            marginBottom="0px"
                            optionFilter={isAgency ? isRealPool : undefined}
                        />
                    </div>
                )}
            {isAgency && integrationType === "dedicatedIntegration" && (
                <React.Fragment>
                    <div className="filter-item">
                        <FilterLabel>Firma</FilterLabel>
                        <CompanyFilter
                            showAllOption={false}
                            showPoolOption={false}
                            allowClear
                            value={companyId}
                            onChange={handleCompanyChange}
                            optionFilter={filterCompanyByDedicatedIntegrationPools}
                            hideTitle
                            selectMarginBottom="0px"
                        />
                    </div>
                    <div className="filter-item">
                        <FilterLabel>Alt Firma</FilterLabel>
                        <SubCompanyFilter
                            company={companyId}
                            value={subCompanyId}
                            onChange={setSubCompanyId}
                            disabled={!companyId}
                            initialCall={false}
                            showAllOption={false}
                            allowClear
                            hideTitle
                            selectMarginBottom="0px"
                        />
                    </div>
                </React.Fragment>
            )}
        </React.Fragment>
    );
};

export default Filters;
